// ============================================================
// server-browser.ts — Server browser for multi-session matches.
// DOM-based UI matching the existing dark military theme.
// ============================================================

import { getToken } from '../../auth/token-store';
import { isMobile } from '../../core/device';

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = getToken();
  const headers: Record<string, string> = { ...(extra ?? {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

const SB_ACCENT = '#e94560';
const SB_BG_DARK = '#1a1a2e';
const SB_BG_PANEL = '#16213e';
const SB_BG_ROW_ALT = '#0f3460';

// ── Types ───────────────────────────────────────────────────

export interface MatchInfo {
  match_id: string;
  player_count: number;
  max_players: number;
  map: string;
  mode: string;
  round_phase: string;
  round_number: number;
  t_score: number;
  ct_score: number;
}

// ── Style injection ─────────────────────────────────────────

let stylesInjected = false;

function injectStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;

  const style = document.createElement('style');
  style.textContent = `
    #sb-overlay {
      position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
      background: rgba(0,0,0,0.92);
      z-index: 150;
      display: none;
      font-family: 'Courier New', monospace;
      color: #ffffff;
      user-select: none;
      overflow-y: auto;
    }

    .sb-container {
      position: relative;
      max-width: 800px;
      margin: 60px auto;
      background: linear-gradient(135deg, ${SB_BG_PANEL}, ${SB_BG_DARK});
      border: 2px solid ${SB_ACCENT};
      border-radius: 6px;
      padding: 30px 36px 36px;
    }

    @media (max-width: 600px) {
      .sb-container {
        margin: 12px;
        padding: 16px;
        max-width: none;
      }
    }

    .sb-title {
      font-size: 28px; font-weight: bold;
      color: ${SB_ACCENT}; letter-spacing: 4px;
      text-transform: uppercase;
      text-shadow: 0 0 20px rgba(233,69,96,0.4);
      margin-bottom: 4px;
      text-align: center;
    }

    .sb-subtitle {
      font-size: 11px; color: #555;
      letter-spacing: 2px; text-transform: uppercase;
      text-align: center;
      margin-bottom: 24px;
    }

    .sb-toolbar {
      display: flex; justify-content: center; gap: 12px;
      margin-bottom: 24px;
    }

    .sb-btn {
      display: inline-block;
      min-height: 44px;
      padding: 12px 28px; font-size: 12px;
      font-weight: bold; letter-spacing: 3px;
      text-transform: uppercase; color: #888;
      background: transparent;
      border: 1px solid #444;
      cursor: pointer;
      font-family: 'Courier New', monospace;
      transition: all 0.15s;
    }
    .sb-btn:hover {
      color: #fff; border-color: #888;
      background: rgba(255,255,255,0.05);
    }
    .sb-btn:disabled {
      opacity: 0.4; cursor: not-allowed;
    }

    .sb-btn-accent {
      color: #fff;
      background: ${SB_ACCENT};
      border: 1px solid ${SB_ACCENT};
    }
    .sb-btn-accent:hover {
      background: #ff6b8a;
      border-color: #ff6b8a;
    }

    .sb-error {
      text-align: center;
      color: #ff4444;
      font-size: 12px;
      letter-spacing: 1px;
      margin-bottom: 16px;
    }

    .sb-table-wrap {
      width: 100%;
      overflow-x: auto;
    }

    .sb-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
      letter-spacing: 1px;
    }

    .sb-table th {
      text-transform: uppercase;
      letter-spacing: 2px;
      font-size: 10px;
      color: ${SB_ACCENT};
      border-bottom: 1px solid ${SB_ACCENT};
      padding: 8px 10px;
      text-align: left;
    }

    .sb-table td {
      padding: 10px 10px;
      border-bottom: 1px solid rgba(255,255,255,0.05);
      white-space: nowrap;
    }

    .sb-table tr.sb-row-even td {
      background: rgba(15,52,96,0.25);
    }

    .sb-table tr:hover td {
      background: rgba(233,69,96,0.08);
    }

    .sb-players-full {
      color: #ff4444;
    }

    .sb-players-open {
      color: #44ff44;
    }

    .sb-empty {
      text-align: center;
      padding: 40px 20px;
      color: #555;
      font-size: 13px;
      letter-spacing: 2px;
      text-transform: uppercase;
    }

    .sb-empty-hint {
      color: #444;
      font-size: 11px;
      margin-top: 8px;
    }

    .sb-join-btn {
      min-height: 44px;
      padding: 12px 20px; font-size: 12px;
      font-weight: bold; letter-spacing: 2px;
      text-transform: uppercase; color: #fff;
      background: ${SB_ACCENT};
      border: 1px solid ${SB_ACCENT};
      border-radius: 2px;
      cursor: pointer;
      font-family: 'Courier New', monospace;
      transition: all 0.15s;
    }
    .sb-join-btn:hover {
      background: #ff6b8a;
      border-color: #ff6b8a;
    }
    .sb-join-btn:disabled {
      opacity: 0.4; cursor: not-allowed;
    }

    .sb-loading {
      text-align: center;
      padding: 30px;
      color: #555;
      font-size: 12px;
      letter-spacing: 2px;
      text-transform: uppercase;
    }

    @media (max-width: 640px) {
      .sb-table-wrap {
        overflow-x: auto;
      }
      .sb-table td {
        white-space: normal;
      }
      /* Hide Phase (col 5) and Score (col 6) on narrow viewports */
      .sb-table th:nth-child(5),
      .sb-table td:nth-child(5),
      .sb-table th:nth-child(6),
      .sb-table td:nth-child(6) {
        display: none;
      }
    }

    /* ── Mobile stacked-card layout (replaces table on mobile) ── */
    .sb-card-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
      width: 100%;
    }

    .sb-card {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 12px;
      border: 1px solid rgba(233,69,96,0.35);
      border-radius: 4px;
      background: rgba(15,52,96,0.25);
      font-size: 13px;
      letter-spacing: 1px;
    }

    .sb-card-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }

    .sb-card-name {
      font-weight: bold;
      color: #fff;
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1 1 auto;
      min-width: 0;
    }

    .sb-card-meta {
      color: #aaa;
      font-size: 12px;
      letter-spacing: 1px;
    }

    /* ── Map preview thumbnails ── */
    .sb-map-cell {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .sb-map-thumb {
      width: 60px;
      height: 40px;
      object-fit: cover;
      border: 1px solid rgba(233,69,96,0.35);
      border-radius: 2px;
      background: ${SB_BG_DARK};
      flex: 0 0 auto;
      display: block;
    }

    .sb-map-thumb-mobile {
      width: 80px;
      height: 52px;
      object-fit: cover;
      border: 1px solid rgba(233,69,96,0.35);
      border-radius: 2px;
      background: ${SB_BG_DARK};
      flex: 0 0 auto;
      display: block;
    }

    .sb-map-thumb-fallback {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1ch;
      min-width: 1ch;
      color: #888;
      font-size: 12px;
      font-family: 'Courier New', monospace;
      flex: 0 0 auto;
    }

    .sb-card-thumb-row {
      display: flex;
      justify-content: flex-start;
      margin-bottom: 4px;
    }
  `;
  document.head.appendChild(style);
}

// ── Map preview helpers ─────────────────────────────────────

/**
 * Build a map-preview thumbnail <img>. Sources from `/maps/<id>/preview.jpg`.
 * On load error, hides the img and inserts a 1ch placeholder (first char of
 * the map name) in its place. The registry currently exposes no preview field,
 * so the JPG path is conventional — onerror is the expected path until assets
 * land in client/public/maps/<id>/preview.jpg.
 */
function buildMapThumb(mapId: string, variant: 'desktop' | 'mobile'): HTMLElement {
  const img = document.createElement('img');
  img.className = variant === 'mobile' ? 'sb-map-thumb-mobile' : 'sb-map-thumb';
  img.alt = mapId;
  img.loading = 'lazy';
  img.decoding = 'async';
  img.src = `/maps/${encodeURIComponent(mapId)}/preview.jpg`;
  img.addEventListener('error', () => {
    img.style.display = 'none';
    const placeholder = document.createElement('span');
    placeholder.className = 'sb-map-thumb-fallback';
    placeholder.textContent = (mapId.charAt(0) || '?').toUpperCase();
    placeholder.title = mapId;
    if (img.parentNode) {
      img.parentNode.insertBefore(placeholder, img);
    }
  });
  return img;
}

// ── ServerBrowser class ─────────────────────────────────────

export class ServerBrowser {
  private serverUrl: string;
  private overlay: HTMLElement | null = null;
  private visible = false;
  private refreshTimer: number | null = null;
  private onJoinCallback: ((matchId: string) => void) | null = null;
  private onBackCallback: (() => void) | null = null;

  constructor(serverUrl: string) {
    this.serverUrl = serverUrl;
  }

  show(): void {
    injectStyles();
    this.ensureOverlay();
    this.visible = true;
    this.overlay!.style.display = 'block';
    this.fetchAndRender();
    this.startAutoRefresh();
  }

  hide(): void {
    this.stopAutoRefresh();
    this.visible = false;
    if (this.overlay) {
      this.overlay.style.display = 'none';
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  setOnJoinMatch(callback: (matchId: string) => void): void {
    this.onJoinCallback = callback;
  }

  setOnBack(callback: () => void): void {
    this.onBackCallback = callback;
  }

  destroy(): void {
    this.stopAutoRefresh();
    this.visible = false;
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    this.onJoinCallback = null;
    this.onBackCallback = null;
  }

  // ── Private ─────────────────────────────────────────────

  private ensureOverlay(): void {
    if (this.overlay) return;

    this.overlay = document.createElement('div');
    this.overlay.id = 'sb-overlay';
    document.body.appendChild(this.overlay);
  }

  private startAutoRefresh(): void {
    this.stopAutoRefresh();
    this.refreshTimer = window.setInterval(() => {
      if (this.visible) {
        this.fetchAndRender();
      }
    }, 5000);
  }

  private stopAutoRefresh(): void {
    if (this.refreshTimer !== null) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private async fetchAndRender(): Promise<void> {
    if (!this.overlay) return;

    // Show loading state only if the table body is empty (first load)
    const existingTable = this.overlay.querySelector('.sb-table-wrap');
    if (!existingTable) {
      this.renderShell(true);
    }

    try {
      const resp = await fetch(`${this.serverUrl}/api/matches`, {
        headers: authHeaders(),
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
      }
      const data = await resp.json();
      const matches: MatchInfo[] = data.matches ?? [];
      this.renderShell(false, matches);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      this.renderShell(false, undefined, message);
    }
  }

  private renderShell(
    loading: boolean,
    matches?: MatchInfo[],
    errorMessage?: string,
  ): void {
    if (!this.overlay) return;
    this.overlay.innerHTML = '';

    const container = document.createElement('div');
    container.className = 'sb-container';

    // Title
    const title = document.createElement('div');
    title.className = 'sb-title';
    title.textContent = 'SERVER BROWSER';
    container.appendChild(title);

    // Subtitle
    const subtitle = document.createElement('div');
    subtitle.className = 'sb-subtitle';
    subtitle.textContent = 'Browse and join active matches';
    container.appendChild(subtitle);

    // Toolbar
    const toolbar = document.createElement('div');
    toolbar.className = 'sb-toolbar';

    const createBtn = document.createElement('button');
    createBtn.className = 'sb-btn sb-btn-accent';
    createBtn.textContent = 'CREATE MATCH';
    createBtn.addEventListener('click', () => this.handleCreateMatch(createBtn));
    toolbar.appendChild(createBtn);

    const refreshBtn = document.createElement('button');
    refreshBtn.className = 'sb-btn';
    refreshBtn.textContent = 'REFRESH';
    refreshBtn.addEventListener('click', () => this.fetchAndRender());
    toolbar.appendChild(refreshBtn);

    const backBtn = document.createElement('button');
    backBtn.className = 'sb-btn';
    backBtn.textContent = 'BACK';
    backBtn.addEventListener('click', () => {
      if (this.onBackCallback) this.onBackCallback();
    });
    toolbar.appendChild(backBtn);

    container.appendChild(toolbar);

    // Error message
    if (errorMessage) {
      const errEl = document.createElement('div');
      errEl.className = 'sb-error';
      errEl.textContent = `Error: ${errorMessage}`;
      container.appendChild(errEl);
    }

    // Content area
    if (loading) {
      const loadEl = document.createElement('div');
      loadEl.className = 'sb-loading';
      loadEl.textContent = 'Loading matches...';
      container.appendChild(loadEl);
    } else if (matches !== undefined) {
      if (matches.length === 0) {
        const emptyEl = document.createElement('div');
        emptyEl.className = 'sb-empty';
        emptyEl.innerHTML =
          'No active matches' +
          '<div class="sb-empty-hint">Create a new match to get started</div>';
        container.appendChild(emptyEl);
      } else if (isMobile()) {
        container.appendChild(this.buildCardList(matches));
      } else {
        container.appendChild(this.buildTable(matches));
      }
    }

    this.overlay.appendChild(container);
  }

  private buildTable(matches: MatchInfo[]): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'sb-table-wrap';

    const table = document.createElement('table');
    table.className = 'sb-table';

    // Header
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');
    const headers = ['Match ID', 'Players', 'Map', 'Mode', 'Phase', 'Score', ''];
    for (const h of headers) {
      const th = document.createElement('th');
      th.textContent = h;
      headerRow.appendChild(th);
    }
    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Body
    const tbody = document.createElement('tbody');
    for (let i = 0; i < matches.length; i++) {
      const m = matches[i];
      const tr = document.createElement('tr');
      if (i % 2 === 0) tr.className = 'sb-row-even';

      // Match ID (truncated to 8 chars)
      const tdId = document.createElement('td');
      tdId.textContent = m.match_id.substring(0, 8);
      tdId.title = m.match_id;
      tr.appendChild(tdId);

      // Players
      const tdPlayers = document.createElement('td');
      const isFull = m.player_count >= m.max_players;
      tdPlayers.className = isFull ? 'sb-players-full' : 'sb-players-open';
      tdPlayers.textContent = `${m.player_count}/${m.max_players}`;
      tr.appendChild(tdPlayers);

      // Map (thumbnail + name)
      const tdMap = document.createElement('td');
      const mapCell = document.createElement('div');
      mapCell.className = 'sb-map-cell';
      mapCell.appendChild(buildMapThumb(m.map, 'desktop'));
      const mapName = document.createElement('span');
      mapName.textContent = m.map;
      mapCell.appendChild(mapName);
      tdMap.appendChild(mapCell);
      tr.appendChild(tdMap);

      // Mode
      const tdMode = document.createElement('td');
      tdMode.textContent = m.mode;
      tr.appendChild(tdMode);

      // Phase
      const tdPhase = document.createElement('td');
      tdPhase.textContent = m.round_phase;
      tr.appendChild(tdPhase);

      // Score
      const tdScore = document.createElement('td');
      tdScore.textContent = `${m.t_score} - ${m.ct_score}`;
      tr.appendChild(tdScore);

      // Action
      const tdAction = document.createElement('td');
      const joinBtn = document.createElement('button');
      joinBtn.className = 'sb-join-btn';
      joinBtn.textContent = 'JOIN';
      joinBtn.disabled = isFull;
      if (!isFull) {
        joinBtn.addEventListener('click', () => {
          if (this.onJoinCallback) this.onJoinCallback(m.match_id);
        });
      }
      tdAction.appendChild(joinBtn);
      tr.appendChild(tdAction);

      tbody.appendChild(tr);
    }

    table.appendChild(tbody);
    wrap.appendChild(table);
    return wrap;
  }

  private buildCardList(matches: MatchInfo[]): HTMLElement {
    const list = document.createElement('div');
    list.className = 'sb-card-list';

    for (const m of matches) {
      const card = document.createElement('div');
      card.className = 'sb-card';

      const isFull = m.player_count >= m.max_players;

      // Row 0: map preview thumbnail (top of card)
      const thumbRow = document.createElement('div');
      thumbRow.className = 'sb-card-thumb-row';
      thumbRow.appendChild(buildMapThumb(m.map, 'mobile'));
      card.appendChild(thumbRow);

      // Row 1: name + JOIN
      const row1 = document.createElement('div');
      row1.className = 'sb-card-row';

      const name = document.createElement('div');
      name.className = 'sb-card-name';
      const shortId = m.match_id.substring(0, 8);
      name.textContent = shortId;
      name.title = m.match_id;
      row1.appendChild(name);

      const joinBtn = document.createElement('button');
      joinBtn.className = 'sb-join-btn';
      joinBtn.textContent = 'JOIN';
      joinBtn.disabled = isFull;
      if (!isFull) {
        joinBtn.addEventListener('click', () => {
          if (this.onJoinCallback) this.onJoinCallback(m.match_id);
        });
      }
      row1.appendChild(joinBtn);
      card.appendChild(row1);

      // Row 2: map • mode • phase
      const row2 = document.createElement('div');
      row2.className = 'sb-card-row sb-card-meta';
      const row2Text = document.createElement('div');
      row2Text.textContent = `${m.map} • ${m.mode} • ${m.round_phase}`;
      row2.appendChild(row2Text);
      card.appendChild(row2);

      // Row 3: players X/Y • ping Xms • score Y
      const row3 = document.createElement('div');
      row3.className = 'sb-card-row sb-card-meta';
      const playersSpan = document.createElement('span');
      playersSpan.className = isFull ? 'sb-players-full' : 'sb-players-open';
      playersSpan.textContent = `${m.player_count}/${m.max_players}`;
      const restSpan = document.createElement('span');
      // Ping is not in the server-list payload; render placeholder.
      restSpan.textContent = ` • ping —ms • score ${m.t_score}-${m.ct_score}`;
      const row3Text = document.createElement('div');
      row3Text.appendChild(playersSpan);
      row3Text.appendChild(restSpan);
      row3.appendChild(row3Text);
      card.appendChild(row3);

      list.appendChild(card);
    }

    return list;
  }

  private async handleCreateMatch(btn: HTMLButtonElement): Promise<void> {
    btn.disabled = true;
    btn.textContent = 'CREATING...';

    try {
      const resp = await fetch(`${this.serverUrl}/api/matches`, {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
      }
      const data = await resp.json();
      const matchId: string = data.match_id;
      if (matchId && this.onJoinCallback) {
        this.onJoinCallback(matchId);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      // Re-render with error
      this.renderShell(false, undefined, `Create failed: ${message}`);
      // Still try to refresh the list
      this.fetchAndRender();
    }
  }
}
